<script setup lang="ts">
import { computed, ref } from 'vue';
import {
  Combobox,
  ComboboxButton,
  ComboboxInput,
  ComboboxOption,
  ComboboxOptions,
} from '@headlessui/vue';
import { autoUpdate, computePosition, flip, offset, shift, size } from '@floating-ui/dom';
import { CheckIcon, ChevronUpDownIcon } from '@heroicons/vue/24/outline';

export interface QueueFilterOption {
  value: string;
  label: string;
}

const props = withDefaults(
  defineProps<{
    options: QueueFilterOption[];
    modelValue: string;
    allLabel?: string;
    includeAllOption?: boolean;
    placeholder?: string;
    searchable?: boolean;
    disabled?: boolean;
    dataTest: string;
  }>(),
  { allLabel: '', includeAllOption: true, placeholder: '', searchable: true, disabled: false }
);

const emit = defineEmits<{ 'update:modelValue': [value: string] }>();

const query = ref('');
const root = ref<HTMLElement | null>(null);

const allOptions = computed<QueueFilterOption[]>(() =>
  props.includeAllOption ? [{ value: '', label: props.allLabel }, ...props.options] : props.options
);

const filteredOptions = computed(() => {
  const trimmed = query.value.trim().toLowerCase();
  if (!props.searchable || !trimmed) return allOptions.value;
  return allOptions.value.filter((option) => option.label.toLowerCase().includes(trimmed));
});

function labelFor(value: string): string {
  return allOptions.value.find((option) => option.value === value)?.label ?? props.allLabel;
}

function onInput(event: Event) {
  query.value = (event.target as HTMLInputElement).value;
}

// Selects the pre-filled label on focus, so typing replaces it instead of inserting mid-text.
function onFocus(event: Event) {
  if (props.searchable) (event.target as HTMLInputElement).select();
}

function onUpdate(value: string) {
  query.value = '';
  emit('update:modelValue', value);
}

// Positions the teleported (see template) options panel against the input,
// since teleporting out of the DOM tree loses that positioning for free.
let stopAutoUpdate: (() => void) | null = null;

function updatePosition(floatingEl: HTMLElement) {
  if (!root.value) return;
  computePosition(root.value, floatingEl, {
    strategy: 'fixed',
    placement: 'bottom-start',
    middleware: [
      offset(4),
      flip({ padding: 8 }),
      shift({ padding: 8 }),
      size({
        padding: 8,
        apply({ availableHeight, rects }) {
          floatingEl.style.maxHeight = `${Math.max(availableHeight, 100)}px`;
          floatingEl.style.minWidth = `${rects.reference.width}px`;
        },
      }),
    ],
  }).then(({ x, y }) => {
    floatingEl.style.left = `${x}px`;
    floatingEl.style.top = `${y}px`;
  });
}

// Ties positioning to the panel's real mount/unmount, which Headless UI
// already drives on open/close — no separate open-state tracking needed.
const vFloat = {
  mounted(el: HTMLElement) {
    el.style.position = 'fixed';
    stopAutoUpdate = autoUpdate(root.value as HTMLElement, el, () => updatePosition(el));
  },
  unmounted() {
    stopAutoUpdate?.();
    stopAutoUpdate = null;
  },
};
</script>

<template>
  <Combobox :model-value="modelValue" :disabled="disabled" @update:model-value="onUpdate">
    <div ref="root" class="relative">
      <ComboboxInput
        :data-test="dataTest"
        :readonly="!searchable"
        :disabled="disabled"
        :placeholder="placeholder"
        :display-value="(value) => labelFor(value as string)"
        class="w-full rounded-lg border border-gray-300 bg-white py-2 pr-9 pl-4 text-sm text-gray-700 disabled:cursor-not-allowed disabled:opacity-50"
        :class="searchable ? 'cursor-text' : 'cursor-pointer'"
        @input="searchable && onInput($event)"
        @focus="onFocus"
      />
      <ComboboxButton
        :data-test="`${dataTest}-toggle`"
        class="absolute inset-y-0 flex items-center px-2.5 disabled:cursor-not-allowed"
        :class="searchable ? 'right-0' : 'inset-x-0 justify-end'"
        :disabled="disabled"
      >
        <ChevronUpDownIcon class="h-4 w-4 text-gray-400" />
      </ComboboxButton>
      <Teleport to="body">
        <ComboboxOptions
          v-float
          class="z-50 min-w-max overflow-auto rounded-md border border-gray-200 bg-white py-1 text-sm shadow-lg focus:outline-none"
        >
          <div v-if="filteredOptions.length === 0" class="px-3 py-2 text-gray-500">No matches</div>
          <ComboboxOption
            v-for="option in filteredOptions"
            :key="option.value"
            v-slot="{ active, selected }"
            :value="option.value"
            as="template"
          >
            <li
              :data-test="`${dataTest}-option-${option.value || 'all'}`"
              class="flex cursor-pointer items-center justify-between gap-4 px-3 py-2"
              :class="active ? 'bg-indigo-500/10' : ''"
            >
              <span :class="selected ? 'font-semibold text-indigo-600' : 'text-gray-700'">
                {{ option.label }}
              </span>
              <CheckIcon v-if="selected" class="h-4 w-4 flex-none text-indigo-600" />
            </li>
          </ComboboxOption>
        </ComboboxOptions>
      </Teleport>
    </div>
  </Combobox>
</template>
